import { useEffect, useMemo, useState, useTransition } from 'react';
import { Typography, Box, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Fab, Snackbar, Alert, Checkbox, CircularProgress, Backdrop } from '@mui/material';
import { Search as SearchIcon, Save as SaveIcon } from '@mui/icons-material';
import AIFeedbackGroup from '../components/AIFeedback/AIFeedbackGroup';
import ConfirmationModal from '../components/ConfirmationModal';
import Layout from '../components/Layout';
import { useParams } from 'react-router-dom';
import { getAiModels, getAIReviewedUVIQuestions, setTargetAccent, updateExpertReviewsForAIFeedback } from '../service/service';

const EXPERT_REVIEW_FIELDS = ['isUserSubmissionCorrectByExpert', 'isAIFeedbackCorrectByExpert'];
const REVIEW_FILTER_OPTIONS = [
  { key: 'submissionChecked', label: 'Submission Review: Checked' },
  { key: 'submissionUnchecked', label: 'Submission Review: Not Checked' },
  { key: 'aiChecked', label: 'AI Feedback Review: Checked' },
  { key: 'aiUnchecked', label: 'AI Feedback Review: Not Checked' },
  { key: 'newlyChecked', label: 'Newly Checked (Before Save)' },
];

const AIFeedbacksTable = () => {
  const { courseId, courseName } = useParams(); // Get the course ID from the URL parameters
  const [aiFeedbacks, setAiFeedbacks] = useState([]);
  const [isInitialPageLoading, setIsInitialPageLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [reviewFilters, setReviewFilters] = useState({
    submissionChecked: false,
    submissionUnchecked: false,
    aiChecked: false,
    aiUnchecked: false,
    newlyChecked: false,
  });
  const [selectedAccent, setSelectedAccent] = useState('');
  const [aiModels, setAiModels] = useState([]);
  const [editedExpertReviews, setEditedExpertReviews] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isFilterPending, startFilterTransition] = useTransition();
  const [snackbarOptions, setSnackbarOptions] = useState({
    open: false,
    severity: 'success',
    message: 'Expert reviews saved successfully.',
  });

  const handleCloseSnackbar = () => {
    setSnackbarOptions({ ...snackbarOptions, open: false });
  };
  
  const getAllAIFeedbacks = async () => {
    try {
      const result = await getAIReviewedUVIQuestions(courseId, courseName);
      setAiFeedbacks(result || []);
      setEditedExpertReviews({});
    } catch (error) {
      console.log('Error', error);
    } finally {
      setIsInitialPageLoading(false);
    }
  };

  const getAllAiModels = async () => {
    try {
      const response = await getAiModels();
      if (response.aiModels && response.aiModels.models) {
        setAiModels(response.aiModels.models || []);
        setSelectedAccent(response.aiModels.targetAccent || '');
      }
    } catch (error) {
      console.log('Error fetching AI models:', error);
    }
  };

  useEffect(() => {
    getAllAiModels();
  }, []);

  const selectAccent = async (accent) => {
    try {
      const targetAccent = accent;
      await setTargetAccent({ targetAccent });
      setSelectedAccent(accent);
    } catch (error) {
      console.log('Error setting target accent:', error);
    }
  };

  useEffect(() => {
    getAllAIFeedbacks();
  }, [courseId, courseName]);

  const handleAccentChange = (event) => {
    const accent = event.target.value;
    selectAccent(accent);
  };

  const handleReviewFiltersSelectChange = (event) => {
    const selectedFilters = event.target.value;

    startFilterTransition(() => {
      setReviewFilters({
        submissionChecked: selectedFilters.includes('submissionChecked'),
        submissionUnchecked: selectedFilters.includes('submissionUnchecked'),
        aiChecked: selectedFilters.includes('aiChecked'),
        aiUnchecked: selectedFilters.includes('aiUnchecked'),
        newlyChecked: selectedFilters.includes('newlyChecked'),
      });
    });
  };

  const selectedReviewFilters = useMemo(
    () =>
      REVIEW_FILTER_OPTIONS.map((option) => option.key).filter(
        (key) => reviewFilters[key],
      ),
    [reviewFilters],
  );

  const handleExpertReviewChange = (feedback, reviewField, reviewValue) => {
    const feedbackId = feedback._id;

    setAiFeedbacks((prev) =>
      prev.map((item) =>
        item._id === feedbackId
          ? { ...item, [reviewField]: reviewValue }
          : item
      )
    );

    setEditedExpertReviews((prev) => {
      const payloadForFeedback = EXPERT_REVIEW_FIELDS.reduce(
        (acc, fieldName) => {
          const value =
            fieldName === reviewField
              ? reviewValue
              : (prev[feedbackId]?.[fieldName] ?? null);

          if (value !== null) {
            acc[fieldName] = value;
          }

          return acc;
        },
        { enrolledQuestionId: feedbackId },
      );

      return {
        ...prev,
        [feedbackId]: payloadForFeedback,
      };
    });
  };

  const pendingReviewStats = useMemo(() => {
    const hasValue = (value) => value !== null && value !== undefined;

    return Object.values(editedExpertReviews).reduce(
      (acc, review) => {
        const submissionChecked = hasValue(review.isUserSubmissionCorrectByExpert);
        const aiFeedbackChecked = hasValue(review.isAIFeedbackCorrectByExpert);

        if (submissionChecked) 
          acc.submissionCheckedCount += 1;

        if (aiFeedbackChecked) 
          acc.aiFeedbackCheckedCount += 1;

        if (submissionChecked || aiFeedbackChecked) 
          acc.pendingRowCount += 1;

        return acc;
      },
      {
        pendingRowCount: 0,
        submissionCheckedCount: 0,
        aiFeedbackCheckedCount: 0,
      },
    );
  }, [editedExpertReviews]);

  const handleSaveExpertReviews = async () => {
    if (!pendingReviewStats.pendingRowCount) return;

    setIsSaving(true);
    try {
      const response = await updateExpertReviewsForAIFeedback(editedExpertReviews);

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to update expert reviews');
      }

      await getAllAIFeedbacks();
      setSnackbarOptions({
        open: true,
        severity: 'success',
        message: 'Expert reviews saved successfully.',
      });
    } catch (error) {
      console.log('Error saving expert reviews:', error);
      setSnackbarOptions({
        open: true,
        severity: 'error',
        message: 'Failed to save expert reviews.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const editedCount = pendingReviewStats.pendingRowCount;
  const submissionCheckedCount = pendingReviewStats.submissionCheckedCount;
  const aiFeedbackCheckedCount = pendingReviewStats.aiFeedbackCheckedCount;

  const filteredFeedbacks = useMemo(() => {
    return aiFeedbacks.filter((feedback) => {
      if (
        emailFilter.trim() &&
        !feedback.userId?.email
          ?.toLowerCase()
          .includes(emailFilter.toLowerCase())
      ) {
        return false;
      }

      const isSubmissionChecked =
        feedback.isUserSubmissionCorrectByExpert !== null &&
        feedback.isUserSubmissionCorrectByExpert !== undefined;
      const isAIFeedbackChecked =
        feedback.isAIFeedbackCorrectByExpert !== null &&
        feedback.isAIFeedbackCorrectByExpert !== undefined;
      const pendingReview = editedExpertReviews[feedback._id] || {};
      const isNewlyCheckedBeforeSave = EXPERT_REVIEW_FIELDS.some(
        (fieldName) =>
          pendingReview[fieldName] !== null &&
          pendingReview[fieldName] !== undefined,
      );

      const hasActiveReviewFilter = Object.values(reviewFilters).some(Boolean);
      if (!hasActiveReviewFilter) {
        return true;
      }

      return (
        (reviewFilters.submissionChecked && isSubmissionChecked) ||
        (reviewFilters.submissionUnchecked && !isSubmissionChecked) ||
        (reviewFilters.aiChecked && isAIFeedbackChecked) ||
        (reviewFilters.aiUnchecked && !isAIFeedbackChecked) ||
        (reviewFilters.newlyChecked && isNewlyCheckedBeforeSave)
      );
    });
  }, [aiFeedbacks, emailFilter, reviewFilters, editedExpertReviews]);

  const groupedFeedbacks = useMemo(() => {
    return filteredFeedbacks.reduce((acc, feedback) => {
      const { parentUnitId } = feedback;
      if (!acc[parentUnitId.name]) acc[parentUnitId.name] = [];
      acc[parentUnitId.name].push(feedback);
      return acc;
    }, {});
  }, [filteredFeedbacks]);

  if (isInitialPageLoading){
    return (
      <Backdrop open={isInitialPageLoading} sx={{ zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }
    
  return (
    <Layout>
      <Typography variant="h4" component="h1" sx={{ mb: 4 }}>
        Review AI Feedbacks
      </Typography>
      
      {/* Filter and Accent Selection Row */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Email Filter */}
        <TextField
          sx={{ maxWidth: 400 }}
          fullWidth
          placeholder="Search by email..."
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }
          }}
        />

        {/* Review Status Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="review-filter-label">Review Status</InputLabel>
          <Select
            labelId="review-filter-label"
            id="review-filter"
            multiple
            value={selectedReviewFilters}
            label="Review Status"
            onChange={handleReviewFiltersSelectChange}
            renderValue={(selected) => {
              if (!selected.length) return 'All';
              return 'Filter applied';
            }}
          >
            {REVIEW_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                <Checkbox checked={reviewFilters[option.key]} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Accent Selector */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="accent-select-label">Target Accent</InputLabel>
          <Select
            labelId="accent-select-label"
            id="accent-select"
            value={selectedAccent}
            label="Target Accent"
            onChange={handleAccentChange}
          >
            {aiModels.map((accent) => (
              <MenuItem key={accent} value={accent}>
                {accent}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isFilterPending && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Applying filters...
          </Typography>
        </Box>
      )}

      {Object.keys(groupedFeedbacks).map((unitName) => (
        <AIFeedbackGroup 
          key={unitName} 
          unitName={unitName} 
          feedbacks={groupedFeedbacks[unitName]} 
          onExpertReviewChange={handleExpertReviewChange}
        />
      ))}

      {editedCount > 0 && (
        <ConfirmationModal
          title="Save Expert Reviews?"
          description={`You are about to save ${editedCount} row${editedCount === 1 ? '' : 's'}.`}
          confirmLabel="Save"
          cancelLabel="Cancel"
          disabled={isSaving}
          loading={isSaving}
          onConfirm={handleSaveExpertReviews}
          renderTrigger={({ openModal, disabled }) => (
            <Fab
              color="primary"
              variant="extended"
              onClick={openModal}
              disabled={disabled}
              sx={{
                position: 'fixed',
                right: 24,
                bottom: 24,
                zIndex: 1300,
              }}
            >
              <SaveIcon sx={{ mr: 1 }} />
              {isSaving ? 'Saving...' : `Save (S: ${submissionCheckedCount}, AI: ${aiFeedbackCheckedCount})`}
            </Fab>
          )}
        >
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Submission reviews checked: {submissionCheckedCount}
            </Typography>
            <Typography variant="body2">
              AI feedback reviews checked: {aiFeedbackCheckedCount}
            </Typography>
          </Box>
        </ConfirmationModal>
      )}

      <Snackbar
        open={snackbarOptions.open}
        autoHideDuration={2500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} 
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarOptions.severity}
          variant="filled"
        >
          {snackbarOptions.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default AIFeedbacksTable;